import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Calendar, Share2, Facebook, Instagram, MessageCircle } from 'lucide-react';

const NewsArticle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [id]);

  const fetchArticle = async () => {
    try {
      const response = await axios.get(`/news/${id}`);
      setArticle(response.data);
    } catch (error) {
      console.error('Error fetching article:', error);
      setError('Article not found');
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = window.location.href;
  const shareText = encodeURIComponent((article?.title || '') + ' - Crawley Athletic FC');

  const handleShare = (platform) => {
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      instagram: `https://www.instagram.com/`, // Instagram doesn't support direct URL sharing
      whatsapp: `https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`
    };

    if (platform === 'instagram') {
      // For Instagram, we'll copy to clipboard since direct sharing isn't supported
      const textToCopy = `${article.title} - ${shareUrl}`;
      
      // Try modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(textToCopy).then(() => {
          showInstagramMessage();
        }).catch(() => {
          fallbackCopyToClipboard(textToCopy);
        });
      } else {
        // Fallback for older browsers or non-HTTPS
        fallbackCopyToClipboard(textToCopy);
      }
      return;
    }

    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const fallbackCopyToClipboard = (text) => {
    // Create temporary textarea element
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      document.execCommand('copy');
      showInstagramMessage();
    } catch (err) {
      console.error('Failed to copy text: ', err);
      showInstagramError();
    }
    
    document.body.removeChild(textArea);
  };

  const showInstagramMessage = () => {
    // Create a custom notification instead of alert
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%);
        color: white;
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        max-width: 350px;
        animation: slideIn 0.3s ease-out;
      ">
        <div style="display: flex; align-items: center; gap: 10px;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
          </svg>
          <div>
            <div style="font-weight: 600; margin-bottom: 4px;">Link copied!</div>
            <div style="font-size: 12px; opacity: 0.9;">Open Instagram and paste the link in your story or post</div>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    // Auto remove after 4 seconds
    setTimeout(() => {
      if (document.body.contains(notification)) {
        notification.style.animation = 'slideOut 0.3s ease-in';
        setTimeout(() => document.body.removeChild(notification), 300);
      }
    }, 4000);
  };

  const showInstagramError = () => {
    const notification = document.createElement('div');
    notification.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #ef4444;
        color: white;
        padding: 16px 20px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        max-width: 350px;
      ">
        <div style="font-weight: 600;">Unable to copy link</div>
        <div style="font-size: 12px; margin-top: 4px;">Please copy the URL manually from your browser</div>
      </div>
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      if (document.body.contains(notification)) {
        document.body.removeChild(notification);
      }
    }, 3000);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-gray-600 mb-6">The news article you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/news')}
            className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800 transition-colors"
          >
            Back to News
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/news')}
            className="flex items-center text-gray-600 hover:text-black transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to News
          </button>
        </div>
      </div>

      {/* Article */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <article className="bg-white rounded-lg shadow-md overflow-hidden">
          {article.image_url && (
            <img 
              src={article.image_url} 
              alt={article.title}
              className="w-full h-96 object-cover"
            />
          )}
          
          <div className="p-8">
            {/* Article Meta */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center text-gray-500">
                <Calendar className="h-4 w-4 mr-2" />
                {new Date(article.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </div>
              
              {/* Share Buttons */}
              <div className="flex items-center space-x-2">
                <Share2 className="h-4 w-4 text-gray-400 mr-2" />
                <button
                  onClick={() => handleShare('facebook')}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                  title="Share on Facebook"
                >
                  <Facebook className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleShare('instagram')}
                  className="p-2 text-pink-600 hover:bg-pink-50 rounded-full transition-colors"
                  title="Copy for Instagram"
                >
                  <Instagram className="h-5 w-5" />
                </button>
                <button
                  onClick={() => handleShare('whatsapp')}
                  className="p-2 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                  title="Share on WhatsApp"
                >
                  <MessageCircle className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Article Title */}
            <h1 className="text-4xl font-bold mb-6">{article.title}</h1>
            
            {/* Article Summary */}
            {article.summary && (
              <p className="text-xl text-gray-600 mb-8 font-medium border-l-4 border-black pl-4">
                {article.summary}
              </p>
            )}

            {/* Article Content */}
            <div className="prose max-w-none text-gray-700 text-lg leading-relaxed">
              <p className="whitespace-pre-line">{article.content}</p>
            </div>

            {/* Share Section at Bottom */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-center space-x-4">
                <span className="text-gray-600 font-medium">Share this article:</span>
                <button
                  onClick={() => handleShare('facebook')}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  <Facebook className="h-4 w-4 mr-2" />
                  Facebook
                </button>
                <button
                  onClick={() => handleShare('instagram')}
                  className="flex items-center px-4 py-2 bg-pink-600 text-white rounded hover:bg-pink-700 transition-colors"
                >
                  <Instagram className="h-4 w-4 mr-2" />
                  Instagram
                </button>
                <button
                  onClick={() => handleShare('whatsapp')}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                >
                  <MessageCircle className="h-4 w-4 mr-2" />
                  WhatsApp
                </button>
              </div>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default NewsArticle;